import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, 
  Users, 
  Target, 
  UserPlus, 
  PlusCircle, 
  LogOut,
  Menu,
  X,
  User,
  ChevronDown,
  Shield,
  Building2,
  Heart
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';

const clubNavItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/players', icon: Users, label: 'Mis Jugadoras' },
  { path: '/needs', icon: Target, label: 'Mis Necesidades' },
  { path: '/my-publications', icon: UserPlus, label: 'Mis Publicaciones' },
];

const adminNavItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'Panel Admin' },
  { path: '/admin/clubs', icon: Building2, label: 'Clubes' },
  { path: '/admin/players', icon: Users, label: 'Jugadoras' },
  { path: '/admin/needs', icon: Target, label: 'Necesidades' },
  { path: '/admin/interests', icon: Heart, label: 'Intereses' },
];

export const DashboardLayout = ({ children }) => {
  const { club, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const isAdmin = club?.is_admin;
  const navItems = isAdmin ? adminNavItems : clubNavItems;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-50/50 flex" data-testid="dashboard-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed lg:sticky top-0 left-0 h-screen w-64 bg-obsidian text-white z-50 transform transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-800">
            <Link to="/dashboard" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
                <span className="font-heading font-bold text-xl">SC</span>
              </div>
              <div>
                <h1 className="font-heading font-bold text-lg tracking-tight">Solo Cracks</h1>
                <span className="text-xs text-slate-400 uppercase tracking-widest">Connect</span>
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {isAdmin && (
              <div className="flex items-center gap-2 px-4 py-2 mb-4 bg-indigo-600/20 rounded-lg border border-indigo-500/30">
                <Shield className="w-4 h-4 text-indigo-400" />
                <span className="text-xs text-indigo-300 uppercase tracking-wider font-semibold">Modo Admin</span>
              </div>
            )}
            
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-link ${location.pathname === item.path || (item.path !== '/admin' && location.pathname.startsWith(item.path)) ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.path.replace(/\//g, '-').slice(1)}`}
              >
                <item.icon className="w-5 h-5" strokeWidth={1.5} />
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}

            {!isAdmin && (
              <div className="pt-4 mt-4 border-t border-slate-800">
                <Link
                  to="/add-player"
                  className="flex items-center gap-3 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-all duration-200"
                  onClick={() => setSidebarOpen(false)}
                  data-testid="nav-add-player"
                >
                  <PlusCircle className="w-5 h-5" strokeWidth={1.5} />
                  <span className="font-medium">Publicar Jugadora</span>
                </Link>
                <Link
                  to="/add-need"
                  className="flex items-center gap-3 px-4 py-3 mt-2 border border-slate-700 hover:bg-slate-800 text-white rounded-lg transition-all duration-200"
                  onClick={() => setSidebarOpen(false)}
                  data-testid="nav-add-need"
                >
                  <Target className="w-5 h-5" strokeWidth={1.5} />
                  <span className="font-medium">Publicar Necesidad</span>
                </Link>
                <Link
                  to="/add-interest"
                  className="flex items-center gap-3 px-4 py-3 mt-2 border border-pink-500/50 hover:bg-pink-600/20 text-pink-300 rounded-lg transition-all duration-200"
                  onClick={() => setSidebarOpen(false)}
                  data-testid="nav-add-interest"
                >
                  <Heart className="w-5 h-5" strokeWidth={1.5} />
                  <span className="font-medium">Me Interesa Jugadora</span>
                </Link>
              </div>
            )}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-slate-300" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{club?.name}</p>
                <p className="text-xs text-slate-400 truncate">{club?.league}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 mt-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-all duration-200"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" strokeWidth={1.5} />
              <span className="font-medium">Cerrar Sesión</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 lg:px-8 h-16">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 hover:bg-slate-100 rounded-lg"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" strokeWidth={1.5} />
            </button>

            <div className="hidden lg:block">
              <h2 className="font-heading font-semibold text-xl text-slate-800">
                {navItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
              </h2>
            </div>

            <div className="flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                    <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                      <span className="text-indigo-600 font-heading font-bold text-sm">
                        {club?.name?.charAt(0)}
                      </span>
                    </div>
                    <span className="hidden md:inline font-medium text-sm">{club?.name}</span>
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Cerrar Sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
